import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { useIsLoggedInQuery } from 'auth/hooks'
import { useUserAuthModal } from 'components/user-auth-dialog/user-auth-dialog-context'
import { Button } from '@repo/design-system-cf/components/button'
import { useUser } from '@repo/cf-user/hooks/use-user-data'
import { AmplitudeEvents, sendAmplitudeEvent } from '@repo/analytics/amplitude/utils'

import { useClubhousesIsMemberQuery, useClubhousesJoinMutation } from '@web/gen/clubhouses'

export function JoinButton({
  slug,
  className,
  clubhouseName,
}: {
  slug: string
  className?: string
  clubhouseName?: string
}) {
  const t = useTranslations('pages.clubhouse.topbanner')
  const queryClient = useQueryClient()
  const { openModal } = useUserAuthModal()
  const isLoggedInQuery = useIsLoggedInQuery()
  const joinMutation = useClubhousesJoinMutation()
  const user = useUser()
  const { data: isMember, isPending } = useClubhousesIsMemberQuery(
    { input: { clubhouseSlug: slug } },
    {
      enabled: isLoggedInQuery.data,
      select: data => data?.clubhousesIsClubhouseMember.isMember,
    },
  )

  const handleClick = () => {
    if (!isLoggedInQuery.data) {
      openModal('register')
      return
    }
    joinMutation.mutateAsync(
      { input: { clubhouseSlug: slug } },
      {
        onSuccess: data => {
          if (data?.clubhousesJoin?.Success) {
            sendAmplitudeEvent(AmplitudeEvents.JOINED_CLUBHOUSE, {
              title: clubhouseName,
            })
            queryClient.invalidateQueries({
              queryKey: ['clubhousesIsMember', { input: { clubhouseSlug: slug } }],
            })
            queryClient.invalidateQueries({
              queryKey: ['clubhousesFromUser', { input: { userId: user.data?.id } }],
            })
          }
        },
      },
    )
  }

  return !isMember && !isPending ? (
    <Button onClick={handleClick} className={className} data-testid="clubhouse-sidebar-join-button">
      {t('cta')}
    </Button>
  ) : null
}
